/**
 * API errors in the browser. Every failed request becomes an `ApiError`
 * carrying the server's stable `code`, a safe message, the request ID (to
 * quote to support) and, for validation errors, per-field messages.
 *
 * This file has no runtime dependencies, so the entry bundle can check
 * errors without loading the client (`client.ts` parses error bodies with
 * the shared Zod contract).
 */

/** A failed API request. `status` is 0 when the request never got an answer. */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly fields: Readonly<Record<string, string>> = {},
  ) {
    super(message);
  }

  /** A 4xx: the request was wrong or not allowed, so retrying will not help. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /** No answer, or the API or its proxy said it is unavailable. */
  get isUnavailable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504;
  }
}

/** The fallback code for each status when the body is not an `ErrorResponse`. */
export function codeForStatus(status: number): string {
  if (status === 0) return 'network_error';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 502 && status <= 504) return 'unavailable';
  return status >= 500 ? 'internal' : 'bad_request';
}

/**
 * Field errors in the shape React Aria's `<Form validationErrors>` takes:
 * the server's input paths without their `body.` or `query.` prefix, so
 * `body.email` lands on the field named `email`.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  return Object.fromEntries(
    Object.entries(error.fields).map(([path, message]) => [
      path.replace(/^(body|query|params)\./, ''),
      message,
    ]),
  );
}

/**
 * The person closed a step-up dialog instead of entering the code: the
 * action they started did not happen, and nothing needs reporting.
 */
export class StepUpCancelledError extends Error {
  override readonly name = 'StepUpCancelledError';

  constructor() {
    super('Step-up cancelled');
  }
}
